import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Sparkles, RefreshCw, Globe, Package, CheckCircle, 
  Lightbulb, TrendingUp, ArrowRight, MapPin, Loader2
} from 'lucide-react';

const AIRecommendations = () => {
  const navigate = useNavigate();
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchRecommendations = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const token = localStorage.getItem('atff_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/ai/personalized-recommendations`, {
        headers,
        // withCredentials removed for CORS compatibility
      });
      
      setRecommendations(response.data);
    } catch (error) {
      console.error('Failed to fetch recommendations:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  const getDemandBadgeColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'high': return 'bg-green-100 text-green-700 border-green-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-gray-100 text-gray-600 border-gray-200';
      default: return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  if (loading) {
    return (
      <Card className="col-span-full">
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center">
            <div className="w-12 h-12 bg-continental/10 rounded-full flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6 text-continental animate-pulse" />
            </div>
            <p className="text-gray-500 font-medium">Generating AI recommendations...</p>
            <p className="text-sm text-gray-400 mt-1">Analyzing your profile and market trends</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const data = recommendations?.recommendations;

  return (
    <Card className="col-span-full border-continental/20" data-testid="ai-recommendations-card">
      <CardHeader className="flex flex-row items-start justify-between pb-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-continental to-continental/70 rounded-xl flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              AI Trade Recommendations
              <Badge variant="outline" className="text-xs bg-tradeGold/10 text-tradeGold border-tradeGold/30">
                GPT-5.2
              </Badge>
            </CardTitle>
            <CardDescription className="text-sm">
              Personalized insights based on your business profile
            </CardDescription>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => fetchRecommendations(true)}
          disabled={refreshing}
          className="text-continental hover:text-continental/80"
          data-testid="refresh-recommendations-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Market Opportunities */}
        {data?.market_opportunities?.length > 0 && (
          <div>
            <h4 className="font-semibold text-charcoal mb-3 flex items-center gap-2">
              <Globe className="w-4 h-4 text-continental" />
              Market Opportunities
            </h4>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {data.market_opportunities.map((market) => (
                <div 
                  key={`${market.country}-${market.demand_level}`}
                  className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer group"
                  onClick={() => navigate(`/marketplace?country=${market.country}`)}
                  data-testid={`market-opportunity-${market.country}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-continental" />
                      <span className="font-semibold text-charcoal">{market.country}</span>
                    </div>
                    <Badge className={`text-xs ${getDemandBadgeColor(market.demand_level)}`}>
                      {market.demand_level}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2">{market.opportunity}</p>
                  <div className="flex items-center gap-1 mt-2 text-continental text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>Explore</span>
                    <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Product Suggestions */}
        {data?.product_suggestions?.length > 0 && (
          <div>
            <h4 className="font-semibold text-charcoal mb-3 flex items-center gap-2">
              <Package className="w-4 h-4 text-tradeGold" />
              Product Suggestions
            </h4>
            <div className="space-y-3">
              {data.product_suggestions.map((product) => (
                <div 
                  key={product.product}
                  className="p-4 bg-gradient-to-r from-tradeGold/5 to-transparent rounded-lg border border-tradeGold/20"
                  data-testid={`product-suggestion-${product.product}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h5 className="font-medium text-charcoal">{product.product}</h5>
                      <p className="text-sm text-gray-600 mt-1">{product.reason}</p>
                      {product.potential_markets?.length > 0 && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-gray-500">Markets:</span>
                          <div className="flex gap-1 flex-wrap">
                            {product.potential_markets.map((market) => (
                              <Badge key={market} variant="outline" className="text-xs">
                                {market}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <TrendingUp className="w-5 h-5 text-tradeGold flex-shrink-0" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Items */}
        {data?.action_items?.length > 0 && (
          <div>
            <h4 className="font-semibold text-charcoal mb-3 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              Recommended Actions
            </h4>
            <ul className="space-y-2">
              {data.action_items.map((action, index) => (
                <li 
                  key={`action-${action.substring(0, 30)}-${index}`}
                  className="flex items-start gap-3 text-sm text-gray-600"
                  data-testid={`action-item-${index}`}
                >
                  <span className="w-5 h-5 bg-continental/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-continental">{index + 1}</span>
                  </span>
                  {action}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Trade Tips */}
        {data?.trade_tips?.length > 0 && (
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
              <Lightbulb className="w-4 h-4" />
              Pro Tips
            </h4>
            <ul className="space-y-2">
              {data.trade_tips.map((tip) => (
                <li key={tip} className="text-sm text-blue-700 flex items-start gap-2">
                  <span className="text-blue-400">•</span>
                  {tip}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Summary */}
        {data?.summary && (
          <div className="pt-4 border-t">
            <p className="text-sm text-gray-600 italic">"{data.summary}"</p>
          </div>
        )}

        {/* CTA Buttons */}
        <div className="flex flex-wrap gap-3 pt-2">
          <Button 
            onClick={() => navigate('/compliance-engine')} 
            size="sm" 
            className="btn-primary"
            data-testid="ai-cta-compliance-btn"
          >
            Check Compliance
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <Button 
            onClick={() => navigate('/marketplace')} 
            size="sm" 
            variant="outline"
            data-testid="ai-cta-marketplace-btn"
          >
            Explore Marketplace
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AIRecommendations;
